import { useEffect, useState } from 'react';
import { pickAuthoredText, type Program } from '@heraion/shared';

import { Icon } from '../Icon/Icon';
import { useLocale } from '../primitives/useLocale';
import { PickerImportList, PickerImportRow } from './PickerImportList';
import type { PickerStyles } from './pickerStyles';
import type { UiTheme } from '../theme';

// #85 (#336, 2B) - the Programs tab's body: the athlete's own and assigned
// programs, then the days of the one tapped. A day tapped adds its exercises
// at the end of the day being edited. Same shape as the Workout tab beside it
// (PickerPlanList, the same PickerImportList): one tap, one import, no
// confirm, because nothing on the day is replaced and Undo takes it back.
export function PickerProgramList({
  theme,
  styles,
  t,
  visible,
  programs,
  onPick,
}: {
  theme: UiTheme;
  styles: PickerStyles;
  t: (key: string, opts?: Record<string, unknown>) => string;
  visible: boolean;
  programs: ReadonlyArray<Program>;
  onPick: (program: Program, sessionIdx: number) => void;
}) {
  const lang = useLocale();
  const [open, setOpen] = useState<Program | null>(null);
  // Every open starts from the list of programs.
  useEffect(() => {
    if (visible) setOpen(null);
  }, [visible]);
  const nameOf = (p: Program) => pickAuthoredText(p.name, lang, p.authorLocale) ?? '';

  if (open) {
    const programName = nameOf(open);
    return (
      <PickerImportList
        theme={theme}
        styles={styles}
        data={open.sessions.map((s, i) => ({ s, i }))}
        keyExtractor={({ i }) => String(i)}
        header={
          <PickerImportRow
            styles={styles}
            title={programName}
            accessibilityLabel={t('composer.importProgramBack', {
              defaultValue: 'Back to the programs',
            })}
            onPress={() => setOpen(null)}
            leading={<Icon name="chevron-left" size={20} color={theme.palette.ink2} />}
          />
        }
        hint={t('composer.importProgramDayHint', {
          defaultValue:
            'Its exercises are added at the end of the day you are editing. Extra rounds are left out, missing ones stay empty.',
        })}
        emptyTitle={t('composer.importProgramEmpty', { defaultValue: 'No other programs yet.' })}
        renderItem={({ s, i }) => {
          const day = pickAuthoredText(s.name, lang, open.authorLocale) ?? `D${i + 1}`;
          const meta = t('composer.importPlanMeta', {
            count: s.slots.length,
            defaultValue: '{{count}} exercises',
          });
          return (
            <PickerImportRow
              styles={styles}
              title={day}
              sub={meta}
              accessibilityLabel={t('composer.importProgramDayAction', {
                day,
                program: programName,
                meta,
                defaultValue: 'Add {{day}} of {{program}} to this day ({{meta}})',
              })}
              onPress={() => onPick(open, i)}
            />
          );
        }}
      />
    );
  }

  return (
    <PickerImportList
      theme={theme}
      styles={styles}
      data={programs}
      keyExtractor={(p) => p.id}
      emptyTitle={t('composer.importProgramEmpty', { defaultValue: 'No other programs yet.' })}
      renderItem={(item) => {
        const meta = [
          t('composer.importProgramDays', {
            count: item.sessions.length,
            defaultValue: '{{count}} days',
          }),
          t('composer.importProgramRounds', {
            count: item.cycleWeeks,
            defaultValue: '{{count}} rounds',
          }),
        ].join(', ');
        return (
          <PickerImportRow
            styles={styles}
            title={nameOf(item)}
            sub={meta}
            accessibilityLabel={t('composer.importProgramOpen', {
              name: nameOf(item),
              meta,
              defaultValue: '{{name}} ({{meta}}): choose a day',
            })}
            onPress={() => setOpen(item)}
            trailing={<Icon name="chevron-right" size={20} color={theme.palette.ink3} />}
          />
        );
      }}
    />
  );
}
